/* ==========================================================================
   Dice Lacrosse PDP — gallery, version selector, variant pack selector,
   position tabs.
   Vanilla JS, no dependencies. Safe to load once for the whole page.
   Sections talk to each other through the `dice:variant` event on document,
   so the sticky bar works even though it is a separate section.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTED = null;

  /* ---------- helpers ---------- */

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function readVariants() {
    var el = document.getElementById('dice-variant-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /* ---------- gallery ---------- */

  function initGallery(root) {
    $all('[data-dice-gallery]', root).forEach(function (gallery) {
      var main = gallery.querySelector('[data-dice-gallery-main]');
      if (!main) return;

      gallery.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-dice-thumb]');
        if (!thumb || !gallery.contains(thumb)) return;

        var src = thumb.getAttribute('data-src');
        var alt = thumb.getAttribute('data-alt') || '';
        if (src) {
          main.setAttribute('src', src);
          main.setAttribute('alt', alt);
          if (main.hasAttribute('srcset')) main.removeAttribute('srcset');
        }

        $all('[data-dice-thumb]', gallery).forEach(function (other) {
          other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
        });
      });
    });
  }

  /* ---------- version + pack selectors ---------- */

  function applyVariant(variant) {
    if (!variant) return;
    SELECTED = variant;

    // Selected state on every version swatch on the page.
    $all('[data-dice-version]').forEach(function (btn) {
      var on = !!variant.version && btn.getAttribute('data-value') === variant.version;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
    });

    // Only show pack rows that belong to the selected version (if any).
    if (variant.version) {
      $all('[data-dice-pack]').forEach(function (row) {
        row.hidden = row.getAttribute('data-version') !== variant.version;
      });
    }

    // Selected state on every pack row on the page.
    $all('[data-dice-pack]').forEach(function (row) {
      var on = row.getAttribute('data-variant-id') === String(variant.id);
      row.setAttribute('aria-checked', on ? 'true' : 'false');
      row.setAttribute('tabindex', on ? '0' : '-1');
    });

    // Every product form on the page points at the selected variant.
    $all('[data-dice-variant-input]').forEach(function (input) {
      input.value = variant.id;
    });

    // CTA price + sold-out state.
    $all('[data-dice-cta-price]').forEach(function (node) {
      node.textContent = variant.price;
    });
    $all('[data-dice-cta], [data-dice-sticky-btn]').forEach(function (button) {
      var label = button.querySelector('[data-dice-cta-label]');
      if (variant.available) {
        button.removeAttribute('disabled');
        if (label && label.dataset.labelDefault) label.textContent = label.dataset.labelDefault;
      } else {
        button.setAttribute('disabled', 'disabled');
        if (label) {
          if (!label.dataset.labelDefault) label.dataset.labelDefault = label.textContent;
          label.textContent = label.dataset.labelSoldout || 'Sold out';
        }
      }
    });

    // Sticky bar mirror.
    $all('[data-dice-sticky-title]').forEach(function (node) {
      node.textContent = variant.title + ' · ' + variant.price;
    });
    $all('[data-dice-sticky-sub]').forEach(function (node) {
      var suffix = node.getAttribute('data-suffix') || '';
      node.textContent = variant.each_label + (suffix ? ' · ' + suffix : '');
    });

    // Keep the URL shareable without adding a history entry per click.
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    document.dispatchEvent(new CustomEvent('dice:variant', { detail: variant }));
  }

  function initVersions(root) {
    var variants = readVariants();
    if (!variants.length) return;

    function selectVersion(value) {
      var candidates = variants.filter(function (v) { return v.version === value; });
      if (!candidates.length) return;

      // Keep the same pack size when switching version, if it still exists.
      var currentPack = SELECTED && SELECTED.pack;
      var next = currentPack && candidates.filter(function (v) { return v.pack === currentPack; })[0];
      if (!next) next = candidates.filter(function (v) { return v.available; })[0] || candidates[0];

      applyVariant(next);
    }

    $all('[data-dice-versions]', root).forEach(function (group) {
      group.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-dice-version]');
        if (!btn || !group.contains(btn)) return;
        selectVersion(btn.getAttribute('data-value'));
      });

      group.addEventListener('keydown', function (event) {
        var keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
        if (keys.indexOf(event.key) === -1) return;

        var btns = $all('[data-dice-version]', group);
        var current = btns.indexOf(event.target.closest('[data-dice-version]'));
        if (current === -1) return;

        event.preventDefault();
        var step = (event.key === 'ArrowDown' || event.key === 'ArrowRight') ? 1 : -1;
        var next = btns[(current + step + btns.length) % btns.length];
        next.focus();
        selectVersion(next.getAttribute('data-value'));
      });
    });
  }

  function initPacks(root) {
    var variants = readVariants();
    if (!variants.length) return;

    function byId(id) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) return variants[i];
      }
      return null;
    }

    $all('[data-dice-packs]', root).forEach(function (group) {
      group.addEventListener('click', function (event) {
        var row = event.target.closest('[data-dice-pack]');
        if (!row || row.hasAttribute('disabled')) return;
        applyVariant(byId(row.getAttribute('data-variant-id')));
      });

      // Arrow keys move between visible rows, like a native radio group.
      group.addEventListener('keydown', function (event) {
        var keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
        if (keys.indexOf(event.key) === -1) return;

        var rows = $all('[data-dice-pack]:not([disabled]):not([hidden])', group);
        var current = rows.indexOf(event.target.closest('[data-dice-pack]'));
        if (current === -1) return;

        event.preventDefault();
        var step = (event.key === 'ArrowDown' || event.key === 'ArrowRight') ? 1 : -1;
        var next = rows[(current + step + rows.length) % rows.length];
        next.focus();
        applyVariant(byId(next.getAttribute('data-variant-id')));
      });
    });

    // Initial selection: ?variant= wins, then the section's default, then first available.
    var fromUrl = new URLSearchParams(window.location.search).get('variant');
    var initial = (fromUrl && byId(fromUrl))
      || byId(document.querySelector('[data-dice-packs]') &&
              document.querySelector('[data-dice-packs]').getAttribute('data-default-variant'))
      || variants.filter(function (v) { return v.available; })[0]
      || variants[0];

    applyVariant(initial);
  }

  /* ---------- position tabs ---------- */

  function initTabs(root) {
    $all('[data-dice-tablist]', root).forEach(function (tablist) {
      var scope = tablist.closest('[data-dice-positions]') || document;

      function select(tab) {
        var id = tab.getAttribute('aria-controls');

        $all('[role="tab"]', tablist).forEach(function (other) {
          var on = other === tab;
          other.setAttribute('aria-selected', on ? 'true' : 'false');
          other.setAttribute('tabindex', on ? '0' : '-1');
        });

        $all('[role="tabpanel"]', scope).forEach(function (panel) {
          panel.hidden = panel.id !== id;
        });
      }

      tablist.addEventListener('click', function (event) {
        var tab = event.target.closest('[role="tab"]');
        if (tab && tablist.contains(tab)) select(tab);
      });

      tablist.addEventListener('keydown', function (event) {
        var tabs = $all('[role="tab"]', tablist);
        var current = tabs.indexOf(document.activeElement);
        if (current === -1) return;

        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(current + 1) % tabs.length];
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(current - 1 + tabs.length) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;

        event.preventDefault();
        next.focus();
        select(next);
      });
    });
  }

  /* ---------- FAQ: keep it single-open ---------- */

  function initFaq(root) {
    $all('[data-dice-faq]', root).forEach(function (list) {
      list.addEventListener('toggle', function (event) {
        var item = event.target;
        if (!item.matches('details') || !item.open) return;
        $all('details', list).forEach(function (other) {
          if (other !== item) other.open = false;
        });
      }, true);
    });
  }

  /* ---------- boot ---------- */

  function init(root) {
    initGallery(root);
    initPacks(root);
    initVersions(root);
    initTabs(root);
    initFaq(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
